import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';

import { TableWrapper } from 'container/styled';
import { CustomTableStyleWrapper } from 'pages/style';
import openNotificationWithIcon from 'utility/notification';
import { CollectionChannelConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionTemplateDto,
  CollectionChannel,
} from '../debtCollectionApi';
import TemplateFormModal from './TemplateFormModal';

const TemplatesTab: React.FC = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<DebtCollectionTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState<{
    visible: boolean;
    editMode: boolean;
    selectedTemplate: DebtCollectionTemplateDto | null;
  }>({ visible: false, editMode: false, selectedTemplate: null });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await debtCollectionApi.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async (id: number) => {
    try {
      await debtCollectionApi.deleteTemplate(id);
      openNotificationWithIcon('success', t('debt-collection:templates.deleted', 'Template deleted'));
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleModalSuccess = () => {
    setModalState({ visible: false, editMode: false, selectedTemplate: null });
    fetchTemplates();
  };

  const columns = [
    {
      title: t('debt-collection:templates.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('debt-collection:templates.channel', 'Channel'),
      dataIndex: 'channel',
      key: 'channel',
      render: (val: CollectionChannel) => {
        const cfg = CollectionChannelConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:templates.subject', 'Subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: (val: string | null) => val || '-',
    },
    {
      title: t('debt-collection:templates.default', 'Default'),
      dataIndex: 'isDefault',
      key: 'isDefault',
      width: 80,
      render: (val: boolean) => (val ? <Tag color="green">Default</Tag> : null),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'actions',
      width: 120,
      render: (_: unknown, record: DebtCollectionTemplateDto) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            className="btn-icon"
            type="primary"
            shape="circle"
            size="small"
            onClick={() =>
              setModalState({ visible: true, editMode: true, selectedTemplate: record })
            }
          >
            <FeatherIcon icon="edit" size={14} />
          </Button>
          <Popconfirm
            title={t('debt-collection:templates.delete-confirm', 'Delete this template?')}
            onConfirm={() => handleDelete(record.id!)}
            okText={t('global:yes', 'Yes')}
            cancelText={t('global:no', 'No')}
          >
            <Button className="btn-icon" type="primary" danger shape="circle" size="small">
              <FeatherIcon icon="trash-2" size={14} />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={() =>
            setModalState({ visible: true, editMode: false, selectedTemplate: null })
          }
        >
          <FeatherIcon icon="plus" size={16} />
          {t('debt-collection:templates.add', 'Add Template')}
        </Button>
      </div>

      <CustomTableStyleWrapper>
        <TableWrapper>
          <Table
            loading={loading}
            dataSource={templates}
            columns={columns}
            rowKey={(r) => String(r.id)}
            pagination={false}
          />
        </TableWrapper>
      </CustomTableStyleWrapper>

      <TemplateFormModal
        visible={modalState.visible}
        editMode={modalState.editMode}
        template={modalState.selectedTemplate}
        onCancel={() =>
          setModalState({ visible: false, editMode: false, selectedTemplate: null })
        }
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default TemplatesTab;
